import { useEffect, useMemo } from "react";
import {
  FaMapMarkerAlt,
  FaExclamationTriangle,
  FaClock,
  FaThumbsUp,
  FaThumbsDown,
  FaArrowRight,
} from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { STATUSES } from "../globals/misc/statuses";
import { fetchPotholes } from "../store/potholeSlice";

const Hero = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { potholes, status } = useSelector((state) => state.pothole);

  useEffect(() => {
    dispatch(fetchPotholes({ limit: 12, sort: "-createdAt" }));
  }, [dispatch]);

  const recent = useMemo(() => potholes.slice(0, 6), [potholes]);

  const severityClass = (severity) => {
    switch (severity) {
      case "high":
        return "bg-red-500 text-white";
      case "medium":
        return "bg-amber-500 text-white";
      case "low":
        return "bg-emerald-500 text-white";
      default:
        return "bg-slate-500 text-white";
    }
  };

  const statusClass = (value) => {
    switch (value) {
      case "in-progress":
        return "bg-amber-100 text-amber-800";
      case "verified":
        return "bg-indigo-100 text-indigo-800";
      case "fixed":
        return "bg-emerald-100 text-emerald-800";
      default:
        return "bg-navy-900/10 text-navy-800";
    }
  };

  return (
    <section className="px-4 py-12 sm:py-16">
      <div className="mx-auto max-w-6xl">
        <div className="mb-8 flex flex-col gap-4 sm:mb-10 sm:flex-row sm:items-end sm:justify-between">
          <div className="max-w-xl animate-rise">
            <p className="mb-2 text-sm font-semibold uppercase tracking-[0.18em] text-amber-600">
              Latest reports
            </p>
            <h2 className="font-display text-3xl font-bold text-navy-900 sm:text-4xl">
              Recent pothole reports
            </h2>
            <p className="mt-2 text-navy-800/70">
              Stay aware of hazards nearby — tap a card for full details.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/potholes")}
            className="inline-flex items-center justify-center gap-2 self-start rounded-xl border border-navy-900/15 bg-white px-4 py-2.5 text-sm font-semibold text-navy-900 transition hover:border-amber-500 hover:text-amber-600 hover:gap-3 sm:self-auto"
          >
            View all
            <FaArrowRight className="text-xs transition-transform" />
          </button>
        </div>

        {status === STATUSES.LOADING && recent.length === 0 ? (
          <div className="flex justify-center py-16">
            <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-navy-800" />
          </div>
        ) : status === STATUSES.ERROR && recent.length === 0 ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-6 py-12 text-center">
            <FaExclamationTriangle className="mx-auto mb-3 text-3xl text-red-500" />
            <p className="font-medium text-red-700">Failed to load reports</p>
          </div>
        ) : recent.length === 0 ? (
          <div className="rounded-2xl border border-navy-900/10 bg-white px-6 py-14 text-center">
            <FaMapMarkerAlt className="mx-auto mb-4 text-4xl text-navy-700/30" />
            <h3 className="font-display text-2xl font-semibold text-navy-900">
              No reports yet
            </h3>
            <p className="mt-2 text-navy-800/65">
              Be the first to report a pothole in your area.
            </p>
            <button
              type="button"
              onClick={() => navigate("/report")}
              className="mt-6 rounded-xl bg-amber-500 px-6 py-2.5 font-semibold text-navy-950 hover:bg-amber-400"
            >
              Report a pothole
            </button>
          </div>
        ) : (
          <>
            {/* Mobile: horizontal snap carousel */}
            <div className="-mx-4 flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-2 scrollbar-thin sm:hidden">
              {recent.map((pothole) => (
                <button
                  key={pothole._id}
                  type="button"
                  onClick={() => navigate(`/pothole/${pothole._id}`)}
                  className="w-[82vw] max-w-xs shrink-0 snap-center overflow-hidden rounded-2xl border border-navy-900/10 bg-white text-left shadow-[0_10px_30px_rgba(11,31,68,0.08)]"
                >
                  <div className="relative h-40 bg-surface">
                    {pothole.images?.url ? (
                      <img
                        src={pothole.images.url}
                        alt={pothole.address}
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-navy-700/30">
                        <FaMapMarkerAlt className="text-3xl" />
                      </div>
                    )}
                    <span
                      className={`absolute left-3 top-3 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase ${severityClass(
                        pothole.severity
                      )}`}
                    >
                      {pothole.severity}
                    </span>
                  </div>
                  <div className="p-4">
                    <h3 className="font-display text-lg font-semibold text-navy-900 line-clamp-2">
                      {pothole.address}
                    </h3>
                    <p className="mt-1 text-sm text-navy-800/65 line-clamp-2">
                      {pothole.description || "No description"}
                    </p>
                    <div className="mt-3 flex items-center justify-between text-xs text-navy-800/60">
                      <span className="inline-flex items-center gap-1">
                        <FaClock />
                        {new Date(pothole.createdAt).toLocaleDateString()}
                      </span>
                      <span className="inline-flex items-center gap-2">
                        <span className="inline-flex items-center gap-1 text-emerald-600">
                          <FaThumbsUp /> {pothole.votes?.upvotes || 0}
                        </span>
                        <span className="inline-flex items-center gap-1 text-red-500">
                          <FaThumbsDown /> {pothole.votes?.downvotes || 0}
                        </span>
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Tablet/Desktop grid */}
            <div className="hidden gap-5 sm:grid sm:grid-cols-2 lg:grid-cols-3">
              {recent.map((pothole, index) => (
                <article
                  key={pothole._id}
                  onClick={() => navigate(`/pothole/${pothole._id}`)}
                  className="group animate-rise cursor-pointer overflow-hidden rounded-2xl border border-navy-900/10 bg-white shadow-[0_10px_30px_rgba(11,31,68,0.06)] transition duration-300 hover:-translate-y-1.5 hover:shadow-[0_18px_40px_rgba(11,31,68,0.12)]"
                  style={{ animationDelay: `${Math.min(index, 5) * 80}ms` }}
                >
                  <div className="relative h-44 overflow-hidden bg-surface lg:h-48">
                    {pothole.images?.url ? (
                      <img
                        src={pothole.images.url}
                        alt={pothole.address}
                        className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-navy-700/30">
                        <FaMapMarkerAlt className="text-3xl" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-navy-950/70 via-transparent to-transparent" />
                    <span
                      className={`absolute left-3 top-3 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase ${severityClass(
                        pothole.severity
                      )}`}
                    >
                      {pothole.severity}
                    </span>
                    <span
                      className={`absolute bottom-3 left-3 rounded-full px-2.5 py-1 text-[11px] font-medium capitalize ${statusClass(
                        pothole.status
                      )}`}
                    >
                      {pothole.status?.replace("-", " ")}
                    </span>
                  </div>

                  <div className="p-4 lg:p-5">
                    <h3 className="font-display text-lg font-semibold leading-snug text-navy-900 line-clamp-2">
                      {pothole.address}
                    </h3>
                    <p className="mt-2 min-h-[2.5rem] text-sm text-navy-800/65 line-clamp-2">
                      {pothole.description || "No description provided."}
                    </p>

                    <div className="mt-4 flex items-center justify-between border-t border-navy-900/8 pt-3 text-sm">
                      <div className="flex items-center gap-3 text-navy-800/65">
                        <span className="inline-flex items-center gap-1 text-emerald-600">
                          <FaThumbsUp /> {pothole.votes?.upvotes || 0}
                        </span>
                        <span className="inline-flex items-center gap-1 text-red-500">
                          <FaThumbsDown /> {pothole.votes?.downvotes || 0}
                        </span>
                      </div>
                      <span className="inline-flex items-center gap-1 text-xs text-navy-800/50">
                        <FaClock />
                        {new Date(pothole.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default Hero;
